import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { IState, ReduxMap } from '../../redux/redux';
import { SecurityRole } from '../../utils/CommonTypes/PeoplePageTypes';
import Button from '../_Core/Button';
import { IBtnProps } from '../_Core/CoreTypes';
import ColorPicker from '../Tools/ColorPicker';
import Modal from '../Modal/Modal';
import './StyleGuidePage.css';

const BRAND_TOKENS = [
  '--style-color-primary',
  '--style-color-primary-hover',
  '--style-color-primary-shadow',
  '--style-color-text-on-primary',
  '--style-color-text-on-primary-offset',
  '--style-color-secondary',
  '--style-color-text-on-secondary',
  '--style-color-text-on-secondary-offset',
  '--style-color-link',
];

const SURFACE_TOKENS = [
  '--style-color-page',
  '--style-color-surface',
  '--style-color-white',
  '--style-color-border',
  '--style-color-text',
  '--style-color-text-muted',
  '--style-color-shadow',
  '--style-color-base-dark',
];

const SEMANTIC_TOKENS = [
  '--style-color-success',
  '--style-color-success-soft',
  '--style-color-success-wash',
  '--style-color-danger',
  '--style-color-danger-strong',
  '--style-color-danger-wash',
  '--style-color-warning',
  '--style-color-warning-bright',
  '--style-color-warning-wash',
  '--style-color-info',
  '--style-color-info-soft',
  '--style-color-info-wash',
];

const SET_TOKENS = [
  '--style-color-set-5-1',
  '--style-color-set-5-2',
  '--style-color-set-5-3',
  '--style-color-set-5-4',
  '--style-color-set-5-5',
];

const TEXT_UTILS = ['text-muted', 'text-muted-2', 'text-muted-3', 'text-active', 'text-active-2', 'text-bold', 'text-soft', 'text-strong', 'text-danger', 'text-light'];

const FILL_UTILS = [
  'fill-muted',
  'fill-muted-2',
  'fill-muted-3',
  'fill-muted-4',
  'fill-muted-5',
  'fill-active',
  'fill-active-2',
  'fill-active-light',
  'fill-strong',
  'fill-soft',
  'fill-warning-1',
  'fill-warning-2',
  'fill-warning-3',
  'fill-alert-1',
  'fill-alert-2',
  'fill-danger',
  'fill-ping-1',
  'fill-ping-2',
  'fill-question-1',
  'fill-question-2',
];

type BtnSample = { type: NonNullable<IBtnProps['type']>; text: string; cls: string };

const BUTTON_SAMPLES: BtnSample[] = [
  { type: 'save', text: 'Save', cls: 'btn-secondary' },
  { type: 'login', text: 'Login', cls: 'btn-secondary' },
  { type: 'new', text: 'New', cls: 'btn-success' },
  { type: 'approve', text: 'Approve', cls: 'btn-success' },
  { type: 'good', text: 'Good', cls: 'btn-success' },
  { type: 'delete', text: 'Delete', cls: 'btn-danger' },
  { type: 'remove', text: 'Remove', cls: 'btn-danger' },
  { type: 'reject', text: 'Reject', cls: 'btn-danger' },
  { type: 'bad', text: 'Bad', cls: 'btn-danger' },
  { type: 'cancel', text: 'Cancel', cls: 'btn-default' },
  { type: 'close', text: 'Close', cls: 'btn-default' },
  { type: 'edit', text: 'Edit', cls: 'btn-default' },
  { type: 'copy', text: 'Copy', cls: 'btn-default' },
  { type: 'register', text: 'Register', cls: 'btn-default' },
  { type: 'download', text: 'Download', cls: 'btn-default' },
  { type: 'upload', text: 'Upload', cls: 'btn-default' },
  { type: 'open', text: 'Open', cls: 'btn-info' },
  { type: 'on', text: 'On', cls: 'btn-info' },
  { type: 'pending', text: 'Pending', cls: 'btn-warning' },
  { type: 'refresh', text: 'Refresh', cls: 'btn only' },
  { type: 'logout', text: 'Logout', cls: 'btn only' },
  { type: 'send', text: 'Send', cls: 'btn only' },
];

const readToken = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const Swatch = ({ name }: { name: string }) => {
  const value = readToken(name);
  return (
    <div className="style-guide-swatch">
      <div className="style-guide-swatch-chip" style={{ background: `var(${name})` }} />
      <div>
        <code>{name}</code>
        <small>{value || '(empty)'}</small>
      </div>
    </div>
  );
};

const StyleGuidePage = (props: IState) => {
  const [previewPrimary, setPreviewPrimary] = useState<string>();
  const [previewSecondary, setPreviewSecondary] = useState<string>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.document.title = 'Style catalog';
  }, []);

  const isStaff = (props.User?.SecurityLevel ?? SecurityRole.Unknown) >= SecurityRole.Staff;
  const chromeMode = props.Settings?.UseOldStyling ? 'old' : 'new';
  const fairName = props.SettingsResponse?.Name || 'this fair';

  const previewStyle = useMemo(() => {
    const style: Record<string, string> = {};
    if (previewPrimary) {
      style['--style-color-primary'] = previewPrimary;
      style['--style-color-primary-hover'] = previewPrimary;
      style['--style-color-link'] = previewPrimary;
    }
    if (previewSecondary) {
      style['--style-color-secondary'] = previewSecondary;
    }
    return style as CSSProperties;
  }, [previewPrimary, previewSecondary]);

  if (!isStaff) {
    return (
      <div className="main-body-page grow style-guide-page" data-testid="style-guide-page">
        <h2>Style catalog</h2>
        <p>Staff only.</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow style-guide-page" data-testid="style-guide-page">
      <h2>Style catalog</h2>
      <p>
        Live samples for <strong>{fairName}</strong>. Chrome mode is <code>{chromeMode}</code>
        {props.Settings?.UseOldStyling ? ' (header/footer via .title-bar / .footer)' : ' (header/footer via .x-style)'}. Page
        content in <code>main-body-page</code> is the same in both modes.
      </p>
      <p className="style-guide-note">
        Staff-only route: <code>/app/dev/style</code>. Rules live in <code>fms-event-app/docs/styling-guide.md</code>. Changing
        a preview color below only affects the dashed box, not the real header/footer.
      </p>

      <nav className="style-guide-toc" aria-label="Style catalog sections">
        <a href="#sg-tokens">Tokens</a>
        <a href="#sg-preview">Preview</a>
        <a href="#sg-buttons">Buttons</a>
        <a href="#sg-forms">Forms</a>
        <a href="#sg-feedback">Alerts / labels</a>
        <a href="#sg-panels">Panels</a>
        <a href="#sg-nav">Nav / lists</a>
        <a href="#sg-utils">status utils</a>
        <a href="#sg-type">Type / space</a>
        <a href="#sg-cards">Cards</a>
        <a href="#sg-overlay">Toast / modal</a>
      </nav>

      <section className="style-guide-section" id="sg-tokens">
        <h3>Live tokens</h3>
        <p className="style-guide-note">
          Computed from <code>:root</code> after <code>GetStyle()</code> / <code>html2</code> loads.
        </p>
        <h4>Brand</h4>
        <div className="style-guide-swatches" key={props.StyleText ? 'styled' : 'default'}>
          {BRAND_TOKENS.map((name) => (
            <Swatch key={name} name={name} />
          ))}
        </div>
        <h4>Surfaces</h4>
        <div className="style-guide-swatches">
          {SURFACE_TOKENS.map((name) => (
            <Swatch key={name} name={name} />
          ))}
        </div>
        <h4>Semantic</h4>
        <div className="style-guide-swatches">
          {SEMANTIC_TOKENS.map((name) => (
            <Swatch key={name} name={name} />
          ))}
        </div>
        <h4>Showcase set-5</h4>
        <div className="style-guide-swatches">
          {SET_TOKENS.map((name) => (
            <Swatch key={name} name={name} />
          ))}
        </div>
      </section>

      <section className="style-guide-section" id="sg-preview">
        <h3>Local preview</h3>
        <div className="style-guide-preview" style={previewStyle}>
          <div className="style-guide-preview-bar">
            <div className="form-group">
              <label className="control-label">Primary</label>
              <ColorPicker value={previewPrimary} onChange={setPreviewPrimary} placeholder="Live skin" />
            </div>
            <div className="form-group">
              <label className="control-label">Secondary</label>
              <ColorPicker value={previewSecondary} onChange={setPreviewSecondary} placeholder="Live skin" />
            </div>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => {
                setPreviewPrimary(undefined);
                setPreviewSecondary(undefined);
              }}>
              Reset
            </button>
          </div>
          <div className="style-guide-row">
            <button type="button" className="btn btn-primary">
              btn-primary
            </button>
            <button type="button" className="btn btn-default">
              btn-default
            </button>
            <span className="label label-primary">label-primary</span>
            <span className="style-guide-on-primary">text on primary</span>
            <span className="style-guide-on-secondary">text on secondary</span>
          </div>
          <div className="progress" style={{ marginTop: 12, marginBottom: 0 }}>
            <div className="progress-bar" style={{ width: '45%' }}>
              progress-bar
            </div>
          </div>
        </div>
      </section>

      <section className="style-guide-section" id="sg-buttons">
        <h3>Buttons</h3>
        <p className="style-guide-note">
          Brand CTA is raw <code>btn btn-primary</code>. <code>Button</code> maps intent to Bootstrap classes.{' '}
          <code>save</code> is <code>btn-secondary</code> (gray), not brand.
        </p>
        <h4>Bootstrap</h4>
        <div className="style-guide-row">
          <button type="button" className="btn btn-primary">
            primary
          </button>
          <button type="button" className="btn btn-default">
            default
          </button>
          <button type="button" className="btn btn-secondary">
            secondary
          </button>
          <button type="button" className="btn btn-success">
            success
          </button>
          <button type="button" className="btn btn-info">
            info
          </button>
          <button type="button" className="btn btn-warning">
            warning
          </button>
          <button type="button" className="btn btn-danger">
            danger
          </button>
          <button type="button" className="btn btn-link">
            link
          </button>
          <button type="button" className="btn btn-primary" disabled>
            disabled
          </button>
          <button type="button" className="btn btn-primary btn-xs">
            xs
          </button>
        </div>
        <h4>Button.tsx</h4>
        <table className="table table-condensed table-striped style-guide-table">
          <thead>
            <tr>
              <th>type</th>
              <th>class</th>
              <th>button</th>
              <th>iconOnly</th>
            </tr>
          </thead>
          <tbody>
            {BUTTON_SAMPLES.map((b) => (
              <tr key={b.type}>
                <td>
                  <code>{b.type}</code>
                </td>
                <td>
                  <code>{b.cls}</code>
                </td>
                <td>
                  <Button type={b.type} text={b.text} onClick={() => undefined} />
                </td>
                <td>
                  <Button type={b.type} iconOnly={true} title={b.text} onClick={() => undefined} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="style-guide-section" id="sg-forms">
        <h3>Forms</h3>
        <form className="form-horizontal" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="sg-name">
              Name
            </label>
            <div className="col-sm-6">
              <input id="sg-name" className="form-control" defaultValue="Ada Lovelace" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="sg-role">
              Role
            </label>
            <div className="col-sm-6">
              <select id="sg-role" className="form-control" defaultValue="judge">
                <option value="participant">Participant</option>
                <option value="judge">Judge</option>
                <option value="volunteer">Volunteer</option>
              </select>
              <span className="help-block">Bootstrap 3 form-control / control-label.</span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" htmlFor="sg-notes">
              Notes
            </label>
            <div className="col-sm-6">
              <textarea id="sg-notes" className="form-control" rows={3} defaultValue="Helper copy stays muted." />
            </div>
          </div>
          <div className="form-group">
            <div className="col-sm-offset-3 col-sm-6">
              <div className="checkbox">
                <label>
                  <input type="checkbox" defaultChecked /> Send confirmation
                </label>
              </div>
            </div>
          </div>
          <div className="form-group">
            <div className="col-sm-offset-3 col-sm-6">
              <button type="submit" className="btn btn-primary">
                Submit
              </button>{' '}
              <button type="button" className="btn btn-default">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="style-guide-section" id="sg-feedback">
        <h3>Alerts and labels</h3>
        <div className="alert alert-success">alert-success — paid / complete</div>
        <div className="alert alert-info">alert-info — informational</div>
        <div className="alert alert-warning">alert-warning — pending / caution</div>
        <div className="alert alert-danger">alert-danger — error / reject</div>
        <div className="style-guide-row">
          <span className="label label-default">default</span>
          <span className="label label-primary">primary</span>
          <span className="label label-success">success</span>
          <span className="label label-info">info</span>
          <span className="label label-warning">warning</span>
          <span className="label label-danger">danger</span>
        </div>
      </section>

      <section className="style-guide-section" id="sg-panels">
        <h3>Panels</h3>
        <p className="style-guide-note">
          <code>theme-bridge.css</code> paints <code>.panel-primary</code> with <code>--style-color-primary</code>, but
          injected <code>SiteStyle.ToCss()</code> overwrites the heading with Basic Site Style (
          <code>ColorPrimaryPanelHeaderBackground</code>, default <code>#0492C7</code>, or secondary when set). That is
          why this heading may not match the header bar.
        </p>
        <div className="row">
          <div className="col-sm-6">
            <div className="panel panel-primary">
              <div className="panel-heading">panel-primary</div>
              <div className="panel-body">Heading color comes from StyleText after the bridge.</div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="panel panel-default">
              <div className="panel-heading">panel-default</div>
              <div className="panel-body">Neutral card. Prefer this for in-page groups.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="style-guide-section" id="sg-nav">
        <h3>Nav, lists, progress</h3>
        <ul className="nav nav-pills">
          <li className="active">
            <a href="#sg-nav">Active</a>
          </li>
          <li>
            <a href="#sg-nav">Other</a>
          </li>
          <li>
            <a href="#sg-nav">Third</a>
          </li>
        </ul>
        <ul className="pagination">
          <li>
            <a href="#sg-nav">«</a>
          </li>
          <li className="active">
            <a href="#sg-nav">1</a>
          </li>
          <li>
            <a href="#sg-nav">2</a>
          </li>
          <li>
            <a href="#sg-nav">»</a>
          </li>
        </ul>
        <div className="list-group" style={{ maxWidth: 360 }}>
          <a href="#sg-nav" className="list-group-item active">
            Active item
          </a>
          <a href="#sg-nav" className="list-group-item">
            Other item
          </a>
        </div>
        <div className="progress">
          <div className="progress-bar" style={{ width: '60%' }}>
            60%
          </div>
        </div>
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Project A</td>
              <td>
                <span className="label label-success">complete</span>
              </td>
            </tr>
            <tr>
              <td>Project B</td>
              <td>
                <span className="label label-warning">pending</span>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="style-guide-section" id="sg-utils">
        <h3>Status utilities</h3>
        <p className="style-guide-note">Status colors, not brand. Do not use these as a skin tint.</p>
        <div className="style-guide-row">
          {TEXT_UTILS.map((cls) => (
            <span key={cls} className={cls} style={{ background: cls === 'text-light' ? 'var(--style-color-muted-3)' : undefined, padding: '0 4px' }}>
              {cls}
            </span>
          ))}
        </div>
        <div className="style-guide-row">
          {FILL_UTILS.map((cls) => (
            <span key={cls} className={`style-guide-fill-demo ${cls}`}>
              {cls}
            </span>
          ))}
        </div>
      </section>

      <section className="style-guide-section" id="sg-type">
        <h3>Type, space, radius</h3>
        <h1>Heading 1</h1>
        <h2>Heading 2</h2>
        <h3>Heading 3</h3>
        <h4>Heading 4</h4>
        <p>
          Body copy uses <code>--style-font-family</code> (Inter). Helper text uses{' '}
          <span className="text-muted">text-muted</span>. Dates still go through <code>FormatDate</code>.
        </p>
        <p>
          In-content link (Bootstrap default, not <code>--style-color-link</code>): <a href="#sg-type">example link</a>
        </p>
        <div className="style-guide-row">
          {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((s) => (
            <span key={s} className="style-guide-space-demo" style={{ padding: `var(--style-space-${s})` }}>
              space-{s}
            </span>
          ))}
          <span className="style-guide-space-demo" style={{ padding: 'var(--style-page-gutter)' }}>
            page-gutter
          </span>
        </div>
        <p className="style-guide-note">
          Mobile (770 / 576): <code>--style-page-gutter</code> and space <code>md</code>+ shrink. Page content must not
          scroll sideways. Images in <code>main-body-page</code> are capped at <code>max-width: 100%</code>.
        </p>
        <div className="style-guide-row">
          {(['sm', 'md', 'lg', 'pill'] as const).map((r) => (
            <span key={r} className="style-guide-radius-demo" style={{ borderRadius: `var(--style-radius-${r})` }}>
              radius-{r}
            </span>
          ))}
        </div>
      </section>

      <section className="style-guide-section" id="sg-cards">
        <h3>Shared content cards</h3>
        <div className="style-guide-tile-row">
          <div className="widget">
            <h3>widget</h3>
            Sidebar / dashboard card. Title sits on secondary.
          </div>
          <div className="quick-access-panel">
            <h3>quick-access-panel</h3>
            Same surface treatment as widgets.
          </div>
          <div className="showcase-project-3">
            <div className="showcase-tile-header">
              <h2>Tile</h2>
            </div>
            <div className="showcase-tile-body">
              <div className="title">showcase-project-3</div>
              <div className="between">
                <div>Category</div>
                <div>P-101</div>
              </div>
            </div>
            <div className="showcase-tile-footer">
              <div className="owners">
                <span>Student Name</span>
              </div>
            </div>
          </div>
          <div className="soft-border" style={{ padding: '1rem', minWidth: 160 }}>
            soft-border
          </div>
        </div>
      </section>

      <section className="style-guide-section" id="sg-overlay">
        <h3>Toast and modal</h3>
        <div className="style-guide-row">
          <button type="button" className="btn btn-success" onClick={() => toast.success('Saved')}>
            toast.success
          </button>
          <button type="button" className="btn btn-danger" onClick={() => toast.error('Failed', { autoClose: false })}>
            toast.error
          </button>
          <button type="button" className="btn btn-info" onClick={() => toast.info('Heads up')}>
            toast.info
          </button>
          <button type="button" className="btn btn-default" onClick={() => setShowModal(true)}>
            Open modal
          </button>
        </div>
      </section>

      {showModal && (
        <Modal title="Style catalog modal" setModalOpen={setShowModal}>
          <p>App Modal, not Bootstrap&apos;s JS modal. Buttons inside still use the same classes.</p>
          <Button type="close" text="Close" onClick={() => setShowModal(false)} />
        </Modal>
      )}
    </div>
  );
};

export default ReduxMap(StyleGuidePage);
